import { writable, derived, readable } from 'svelte/store';
// Initial state
const initialState = {
    videos: [],
    keywords: [],
    currentView: 'universe',
    isLoading: false,
    error: null,
    cameraPosition: { x: 0, y: 0, z: 1000 },
    selectedVideo: null,
    extractionOptions: {
        minWordLength: 4,
        maxKeywords: 10,
        customStopWords: []
    },
    searchQuery: '',
    activeKeywordFilters: []
};
// Main store
export const visualizationStore = writable(initialState);
// Derived stores for specific data
export const videos = derived(visualizationStore, ($store) => {
    const query = $store.searchQuery.trim().toLowerCase();
    const filters = new Set($store.activeKeywordFilters.map(k => k.toLowerCase()));
    return $store.videos.filter(v => {
        const matchesQuery = !query
            || v.title.toLowerCase().includes(query)
            || v.description.toLowerCase().includes(query)
            || v.keywords.some(k => k.toLowerCase().includes(query));
        const matchesFilters = filters.size === 0
            || v.keywords.some(k => filters.has(k.toLowerCase()));
        return matchesQuery && matchesFilters;
    });
});
export const keywords = derived(visualizationStore, ($store) => {
    const query = $store.searchQuery.trim().toLowerCase();
    if (!query)
        return $store.keywords;
    return $store.keywords.filter(k => k.keyword.toLowerCase().includes(query));
});
export const currentView = derived(visualizationStore, ($store) => $store.currentView);
export const isLoading = derived(visualizationStore, ($store) => $store.isLoading);
// UI helper: ensure the loading spinner stays visible for a minimum duration
const MIN_SPINNER_MS = 1500; // adjust as desired
export const uiIsLoading = readable(false, (set) => {
    let timer;
    let loadingStartedAt = 0;
    const unsubscribe = visualizationStore.subscribe(($store) => {
        if ($store.isLoading) {
            loadingStartedAt = Date.now();
            if (timer)
                clearTimeout(timer);
            set(true);
        }
        else {
            const elapsed = Date.now() - loadingStartedAt;
            const remaining = Math.max(0, MIN_SPINNER_MS - elapsed);
            if (timer)
                clearTimeout(timer);
            timer = setTimeout(() => set(false), remaining);
        }
    });
    return () => {
        if (timer)
            clearTimeout(timer);
        unsubscribe();
    };
});
export const error = derived(visualizationStore, ($store) => $store.error);
export const selectedVideo = derived(visualizationStore, ($store) => $store.selectedVideo);
// Actions
export const visualizationActions = {
    setLoading: (loading) => {
        visualizationStore.update(state => ({
            ...state,
            isLoading: loading,
            error: loading ? null : state.error
        }));
    },
    setError: (error) => {
        visualizationStore.update(state => ({
            ...state,
            error,
            isLoading: false
        }));
    },
    setVideos: (videos) => {
        visualizationStore.update(state => ({
            ...state,
            videos,
            isLoading: false,
            error: null
        }));
    },
    setKeywords: (keywords) => {
        visualizationStore.update(state => ({
            ...state,
            keywords
        }));
    },
    setCurrentView: (view) => {
        visualizationStore.update(state => ({
            ...state,
            currentView: view
        }));
    },
    setCameraPosition: (position) => {
        visualizationStore.update(state => ({
            ...state,
            cameraPosition: position
        }));
    },
    selectVideo: (videoId) => {
        visualizationStore.update(state => ({
            ...state,
            selectedVideo: videoId
        }));
    },
    setExtractionOptions: (options) => {
        visualizationStore.update(state => ({
            ...state,
            extractionOptions: { ...state.extractionOptions, ...options }
        }));
    },
    reset: () => {
        visualizationStore.set(initialState);
    },
    setSearchQuery: (query) => {
        visualizationStore.update(state => ({
            ...state,
            searchQuery: query
        }));
    },
    toggleKeywordFilter: (keyword) => {
        visualizationStore.update(state => {
            const exists = state.activeKeywordFilters.includes(keyword);
            const next = exists
                ? state.activeKeywordFilters.filter(k => k !== keyword)
                : [...state.activeKeywordFilters, keyword];
            return { ...state, activeKeywordFilters: next };
        });
    },
    clearFilters: () => {
        visualizationStore.update(state => ({
            ...state,
            activeKeywordFilters: []
        }));
    }
};
